// Модель отображения загрузки машин для КП МСК Газобетона.
//
// Ничего не считает заново: раскладывает готовые значения из
// offer.delivery.deliveryBreakdown и из уже собранных строк доставки блоков КП.
// Стоимость доставки, число рейсов и выбор транспорта берутся из расчёта —
// повторять их в интерфейсе нельзя.

import {
  loadedWord,
  fullWord,
  partialWord,
  vehiclesWord,
  vehicleTypeBreakdown,
} from '../../utils/gazDeclensions.js';
import { buildGazVehicleLoadView } from './gazVehicleLoadView.js';
import { isGazUnloadingWithSlingers } from './gazSlingerNotice.js';

const parseNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.replace(/\s/g, '').replace(',', '.');
  if (normalized === '') {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const positive = (value) => {
  const parsed = parseNumber(value);
  return parsed !== null && parsed > 0 ? parsed : null;
};

// Тип транспорта берём из уже сформированных строк доставки, а не выбираем заново.
const VEHICLE_SHALANDA = 'Шаланда';
const VEHICLE_MANIPULATOR = 'Манипулятор';
// Кран товар не возит вовсе: он бывает только отдельной техникой на разгрузке.
const VEHICLE_CRANE = 'Кран';

const findDeliveryRow = (blocks, key) => {
  if (!Array.isArray(blocks)) {
    return null;
  }
  for (const block of blocks) {
    const rows = Array.isArray(block?.rows) ? block.rows : [];
    const row = rows.find((candidate) => candidate?.key === key);
    if (row) {
      return row;
    }
  }
  return null;
};

// Позиции, чьё паллетоместо посчитать не из чего (норма поддона не заведена).
// Такой груз всё равно занимает место, поэтому машину с ним нельзя объявлять
// полной и предлагать к дополнению.
const collectUnmeasuredCargo = (blocks) => {
  if (!Array.isArray(blocks)) {
    return [];
  }
  const names = [];
  for (const block of blocks) {
    const blockNames = Array.isArray(block?.unmeasuredCargoNames) ? block.unmeasuredCargoNames : [];
    for (const name of blockNames) {
      if (name && !names.includes(name)) {
        names.push(name);
      }
    }
  }
  return names;
};

const detectRemainderVehicleType = (blocks, breakdown) => {
  if (findDeliveryRow(blocks, 'secondary-manipulator-trip')) {
    return VEHICLE_MANIPULATOR;
  }
  if (findDeliveryRow(blocks, 'secondary-manipulator-trip-no-unload')) {
    return VEHICLE_MANIPULATOR;
  }
  if (findDeliveryRow(blocks, 'secondary-shalanda')) {
    return VEHICLE_SHALANDA;
  }
  // Строки доставки для остатка нет — опираемся на признак из расчёта.
  if (breakdown?.manipulatorGoesWithShalands === true) {
    return VEHICLE_MANIPULATOR;
  }
  return null;
};

/**
 * Техника, которая приезжает без груза и только разгружает заказ.
 *
 * Это услуга, а не рейс с товаром, поэтому в список машин она не попадает и число
 * рейсов не меняет: рейсы считает сервер, интерфейс их только показывает. Способ
 * разгрузки берётся из расчёта, а не из названия транспорта.
 *
 * Манипулятор попадает сюда, только когда разгрузка назначена манипулятором, сам
 * он с шаландами не едет и оплачены часы «встречи». Часы > 0 ставит единственная
 * ветка расчёта — та самая, где весь заказ уехал шаландами
 * (`getManipulatorMeetingHours`). В остальных режимах их нет: «без разгрузки» и
 * одиночный манипулятор возвращают ноль. Когда манипулятор везёт товар и
 * разгружает его, `manipulatorGoesWithShalands` истинно — это обычная грузовая
 * машина, и своей карточки услуги у неё не появляется.
 *
 * Кран отдельной машиной приезжает всегда, когда выбрана разгрузка краном: своего
 * рейса с товаром у него не бывает, часов расчёт по нему не ведёт, а стоимость —
 * фиксированная подача на весь заказ (`craneUnloadingCost`).
 *
 * Поддоны и сумма приходят посчитанными из расчёта; разгрузка уже входит в его
 * итог, и карточка её только называет. Своих чисел и запасных значений здесь нет:
 * чего расчёт не дал, то и не показывается.
 */
const buildUnloadingService = (breakdown) => {
  const method = String(breakdown?.unloadMethod || '').trim().toLowerCase();

  if (method === 'разгрузка краном') {
    return {
      type: VEHICLE_CRANE,
      unloadedPallets: positive(breakdown.shalandaPallets) || positive(breakdown.totalPallets),
      unloadingCost: positive(breakdown.craneUnloadingCost),
      // Кран приезжает сам по себе, и уточнение о стропальщиках относится не к
      // нему одному, а ко всей разгрузке на объекте: его показывает общий блок
      // под машинами. Внутри карточки второй копии быть не должно.
      showsSlingers: false,
    };
  }

  if (method !== 'разгрузка манипулятором') {
    return null;
  }
  if (breakdown.manipulatorGoesWithShalands === true) {
    return null;
  }
  const hours = positive(breakdown.unloadingHours);
  if (!hours) {
    return null;
  }

  return {
    type: VEHICLE_MANIPULATOR,
    hours,
    unloadedPallets: positive(breakdown.shalandaPallets) || positive(breakdown.totalPallets),
    unloadingCost: positive(breakdown.unloadingCost),
    // Разгружает именно эта машина — блок стоит в её карточке.
    showsSlingers: true,
  };
};

const round3 = (value) => Math.round((Number(value) || 0) * 1000) / 1000;

const findBlock = (blocks, key) => (Array.isArray(blocks) ? blocks : [])
  .find((block) => block?.key === key) || null;

/**
 * Состав газобетонного груза машины.
 *
 * Берётся готовым из блока КП (`gazCargoItems`) — это тот же расклад по основному
 * и остаточному блоку, по которому посчитаны суммы. Своего распределения здесь нет.
 *
 * Группа одинаковых полных шаланд: в карточке заголовок описывает ОДНУ машину
 * (`loadedPallets` = вместимость одной шаланды), а блок хранит груз всей группы.
 * Разложить груз по конкретным машинам внутри группы данные позволяют только когда
 * номенклатура одна: тогда деление на число машин точное. При нескольких
 * номенклатурах распределение между машинами группы нигде не хранится — выдавать
 * догадку за факт нельзя, поэтому показывается суммарный груз группы с честной
 * подписью.
 *
 * @returns {{ items: Array, scope: 'vehicle'|'group', note: string|null }}
 */
const buildVehicleCargo = ({ block, count }) => {
  const source = Array.isArray(block?.gazCargoItems) ? block.gazCargoItems : [];
  const items = source.filter((item) => Number(item?.volume) > 0);

  if (items.length === 0) {
    return { items: [], scope: 'vehicle', note: null };
  }

  if (count <= 1) {
    return { items, scope: 'vehicle', note: null };
  }

  // Одна номенклатура — состав одной машины восстанавливается точно.
  if (items.length === 1) {
    const [item] = items;
    return {
      items: [{
        ...item,
        volume: round3(item.volume / count),
        pallets: item.pallets === null ? null : round3(item.pallets / count),
        pieces: item.pieces === null ? null : round3(item.pieces / count),
      }],
      scope: 'vehicle',
      note: `В каждой из ${count} шаланд`,
    };
  }

  // Несколько номенклатур — разложить по машинам нечем.
  return { items, scope: 'group', note: `Груз во всех ${count} шаландах` };
};

// Занятость и свободное место считает общая функция представления: тем же расчётом
// пользуется подпись блока КП, поэтому карточка и подпись не могут разойтись.
const buildVehicle = ({ type, count, capacity, loadedPallets, palletVolume, hasUnmeasuredCargo = false }) => ({
  type,
  count,
  ...buildGazVehicleLoadView({ capacity, loadedPallets, palletVolume, hasUnmeasuredCargo }),
});

/**
 * Строка «Доставка: …» для верхней сводки. Собирается динамически:
 *   «Доставка: 10 машин — 9 полных, манипулятор загружен на 50%»
 *   «Доставка: 10 машин — все загружены полностью»
 *   «Доставка: 10 машин — 8 полных, 2 неполные»
 *   «Доставка: 1 машина — манипулятор загружен на 50%»
 */
export const buildVehiclesSummaryText = (model) => {
  if (!model || !Array.isArray(model.vehicles) || model.vehicles.length === 0) {
    return null;
  }

  const total = model.totalVehicles;
  const head = `Доставка: ${total} ${vehiclesWord(total)}`;

  const fullVehicles = model.vehicles.filter((vehicle) => vehicle.isFull && !vehicle.isOverloaded);
  const incompleteVehicles = model.vehicles.filter((vehicle) => !vehicle.isFull || vehicle.isOverloaded);

  const fullCount = fullVehicles.reduce((sum, vehicle) => sum + vehicle.count, 0);
  const incompleteCount = incompleteVehicles.reduce((sum, vehicle) => sum + vehicle.count, 0);

  if (incompleteCount === 0) {
    return total === 1
      ? `${head} — ${model.vehicles[0].type.toLowerCase()} ${loadedWord(model.vehicles[0].type)} полностью`
      : `${head} — все загружены полностью`;
  }

  const parts = [];
  if (fullCount > 0) {
    parts.push(`${fullCount} ${fullWord(fullCount, fullVehicles[0].type)}`);
  }

  // Сколько мест свободно — сказано один раз, в карточке своей машины. Здесь только
  // состав рейса: сколько машин полных и сколько неполных. Иначе одно и то же число
  // встречается на экране дважды.
  parts.push(`${incompleteCount} ${partialWord(incompleteCount, incompleteVehicles[0].type)}`);

  return `${head} — ${parts.join(', ')}`;
};

/**
 * Подсказка на строке рейсов блока КП. Числа берутся из модели, поэтому совпадают
 * с карточкой машины и подписью блока. Свободное место есть только у машины с
 * остатком; со строкой её сопоставляем по типу транспорта, а не по позиции строки.
 *
 * @param {object} model  — результат buildGazVehicleLoadModel
 * @param {string} rowKey — ключ строки доставки в блоке КП
 */
export const buildGazTripsTooltipText = (model, rowKey) => {
  const rowVehicleType = rowKey === 'secondary-shalanda'
    ? VEHICLE_SHALANDA
    : (rowKey === 'secondary-manipulator-trip' || rowKey === 'secondary-manipulator-trip-no-unload'
      ? VEHICLE_MANIPULATOR
      : null);

  if (!rowVehicleType) {
    return null;
  }

  const remainderVehicle = (model?.vehicles || []).find((vehicle) => vehicle.isRemainder);

  // Строке соответствуют только полные машины — свободного места в них нет.
  if (!remainderVehicle || remainderVehicle.type !== rowVehicleType) {
    return 'Машина загружена полностью';
  }

  const load = `Загрузка: ${remainderVehicle.loadedPallets} из ${remainderVehicle.capacity} подд.`;

  if (remainderVehicle.hasUnmeasuredCargo) {
    return `${load} | Свободное место не определено: нет нормы поддона`;
  }

  return remainderVehicle.freePallets > 0
    ? `${load} | Свободно: ${remainderVehicle.freePallets}`
    : load;
};

/** «2 шаланды + 1 манипулятор» — одинаковые типы сгруппированы. */
export const buildVehicleTypesText = (model) => (
  model && Array.isArray(model.vehicles) ? vehicleTypeBreakdown(model.vehicles) : null
);

/**
 * Собирает модель загрузки машин.
 * @param {object} offer   — оффер с delivery.deliveryBreakdown
 * @param {Array}  blocks  — блоки КП из buildGazCommercialProposalBlocks
 */
export const buildGazVehicleLoadModel = (offer, blocks) => {
  const delivery = offer?.delivery;
  if (!delivery) {
    return null;
  }

  // Самовывоз — машин нет.
  if (String(delivery.name || '').trim().toLowerCase() === 'самовывоз') {
    return null;
  }

  const breakdown = delivery.deliveryBreakdown;
  if (!breakdown || typeof breakdown !== 'object') {
    return null;
  }

  const shalandaCapacity = positive(breakdown.shalandaCapacity);
  const manipulatorCapacity = positive(breakdown.manipulatorCapacity)
    || positive(breakdown.manipulator5tCapacity);
  const palletVolume = positive(breakdown.dominantPalletVolume);

  const fullShalandaCount = Math.max(Math.floor(parseNumber(breakdown.fullShalandaCount) || 0), 0);
  const remainderPallets = Math.max(parseNumber(breakdown.remainderPallets) || 0, 0);
  const unmeasuredCargo = collectUnmeasuredCargo(blocks);

  const vehicles = [];

  // 1. Полные шаланды одинаковы — группируем множителем.
  if (fullShalandaCount > 0 && shalandaCapacity) {
    const mainCargo = buildVehicleCargo({
      block: findBlock(blocks, 'main'),
      count: fullShalandaCount,
    });

    vehicles.push({
      ...buildVehicle({
        type: VEHICLE_SHALANDA,
        count: fullShalandaCount,
        capacity: shalandaCapacity,
        loadedPallets: shalandaCapacity,
        palletVolume,
      }),
      isRemainder: false,
      unloadsCargo: false,
      cargoItems: mainCargo.items,
      cargoScope: mainCargo.scope,
      cargoNote: mainCargo.note,
    });
  }

  // 2. Машина с остатком — отдельной карточкой.
  if (remainderPallets > 0) {
    const remainderType = detectRemainderVehicleType(blocks, breakdown);
    const capacity = remainderType === VEHICLE_MANIPULATOR
      ? (manipulatorCapacity || shalandaCapacity)
      : shalandaCapacity;

    if (capacity) {
      const remainderVehicle = buildVehicle({
        type: remainderType || VEHICLE_SHALANDA,
        count: 1,
        capacity,
        loadedPallets: remainderPallets,
        palletVolume,
        hasUnmeasuredCargo: unmeasuredCargo.length > 0,
      });

      const remainderCargo = buildVehicleCargo({
        block: findBlock(blocks, 'secondary'),
        count: 1,
      });

      vehicles.push({
        ...remainderVehicle,
        // Свой груз манипулятор снимает всегда — стрелу он привозит с собой
        // (правило I спеки), и в режиме «без разгрузки» тоже, просто бесплатно.
        // Оплачена услуга или нет, поддоны кто-то должен цеплять, поэтому
        // уточнение о стропальщиках стоит в его карточке при любом способе
        // разгрузки. Шаланды сюда не попадают: они не разгружают.
        unloadsCargo: remainderType === VEHICLE_MANIPULATOR,
        cargoItems: remainderCargo.items,
        cargoScope: remainderCargo.scope,
        cargoNote: remainderCargo.note,
        typeIsKnown: Boolean(remainderType),
        // Машина остатка помечается явно: после округления свободных мест вниз она
        // может оказаться полной, и искать её по признаку «неполная» уже нельзя.
        isRemainder: true,
        unmeasuredCargoNames: unmeasuredCargo,
      });
    }
  }

  if (!vehicles.length) {
    return null;
  }

  // Грузовые машины и только они: разгрузочный манипулятор товар не везёт, и в
  // числе рейсов ему места нет.
  const totalVehicles = vehicles.reduce((sum, vehicle) => sum + vehicle.count, 0);

  return {
    vehicles,
    totalVehicles,
    unloadingService: buildUnloadingService(breakdown),
    // Нужны ли стропальщики. Признак модели, а не машины: при разгрузке краном
    // разгружает не машина заказа, и повесить предупреждение не на что — карточка
    // крана его намеренно не берёт.
    // Манипулятор с грузом добавляется отдельным слагаемым — он снимает своё
    // и без оплаченной разгрузки (правило I).
    needsSlingers: isGazUnloadingWithSlingers(breakdown)
      || vehicles.some((vehicle) => vehicle.unloadsCargo),
    hasIncompleteVehicle: vehicles.some((vehicle) => !vehicle.isFull),
    hasUnmeasuredCargo: unmeasuredCargo.length > 0,
    unmeasuredCargoNames: unmeasuredCargo,
  };
};

export default buildGazVehicleLoadModel;
